/**
 * INITIALIZING THE IFRAME
 * To send data to an iframe, a @type {ref} needs to be attached to the iframe.
 * Add the ref to the iframe store with @function iframe/addIframeRef on the Vue
 * life cycle @function mounted and @function iframe/removeIframeRef on the Vue
 * life cycle @function beforeDestroy
 *
 * @example
 * <Iframe
 *  ref="iframe_"
 *  src="..."
 * />
 *
 * @example
 * mounted() {
 *   this.addIframeRef(this.$refs.iframe_)
 * },
 * beforeDestroy() {
 *    this.removeIframeRef(this.$refs.iframe_)
 * },
 * methods: {
 *  ...mapActions('iframe', ['addIframeRef', 'removeIframeRef']),
 * },
 */

/**
 * SENDING IFRAME EVENTS
 * The store method @function iframe/postIframeMessage can then be used in the
 * Vue component. The object structure which the data should be sent in can be
 * seen in the iframe React code.
 * https://git.adriel.com/adriel/adriel-fe-react/-/blob/main/iframeMessage/types.ts
 *
 * @example
 * methods: {
 *   ...mapActions('iframe', ['postIframeMessage']),
 * },
 *
 *
 * @example
 * <button onClick={() => {
 *   this.postIframeMessage({
 *     type: `CLICK_ANYWHERE`,
 *     origin: `VUE`,
 *   });
 * }}>Vue button</button>
 */

export default {
    namespaced: 'true',
    state: {
        iframeRefs: [],
        rectFromReact: undefined,
        pendingInviteMemberModal: false,
        pendingCreateWorkspaceModal: false,
    },
    getters: {
        iframeRefs: ({ iframeRefs }) => iframeRefs,
    },
    mutations: {
        insertIframeRef(state, ref) {
            state.iframeRefs.push(ref);
        },
        deleteIframeRef(state, ref) {
            state.iframeRefs = state.iframeRefs.filter((iRef) => iRef !== ref);
        },
        setRectFromReact(state, rect) {
            state.rectFromReact = rect;
        },
        setPendingInviteMemberModal(state, value) {
            state.pendingInviteMemberModal = value;
        },
        setPendingCreateWorkspaceModal(state, value) {
            state.pendingCreateWorkspaceModal = value;
        },
    },
    actions: {
        addIframeRef({ commit }, ref) {
            commit(`insertIframeRef`, ref);
        },
        removeIframeRef({ commit }, ref) {
            commit(`deleteIframeRef`, ref);
        },
        setPendingInviteMemberModal({ commit }, value) {
            commit('setPendingInviteMemberModal', value);
        },
        setPendingCreateWorkspaceModal({ commit }, value) {
            commit('setPendingCreateWorkspaceModal', value);
        },
        postIframeMessage({ getters }, iframeEvent) {
            if (process.env.NODE_ENV === `development`) {
                console.log(`Vue - postIframeMessage`, iframeEvent);
            }
            const { iframeRefs } = getters;
            if (!iframeRefs.length) {
                return;
            }
            const targetOrigin = `*`; // `*` indicates no preference
            iframeRefs.forEach((iframeRef) => {
                // the below object chaining depends on the structure of the <Iframe> component
                const contentWindow =
                    iframeRef?.$children?.[0].$children?.[0].$el.contentWindow;

                contentWindow?.postMessage(iframeEvent, targetOrigin);
            });
        },
    },
};
